import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BadgeDisplay } from '@/components/training/BadgeDisplay';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ArrowLeft, Award, Download, Calendar, Trophy, Printer, X } from 'lucide-react';

interface TrainingCompletion {
  id: string;
  user_id: string;
  module_id: string;
  completed_at: string;
  quiz_score: number;
  badge_level: 'gold' | 'silver' | 'bronze' | null;
  certificate_url: string | null;
  time_spent_minutes: number;
  training_modules: {
    title_en: string;
    title_el: string;
    description_en: string;
    description_el: string;
  };
}

const CertificatesPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [completions, setCompletions] = useState<TrainingCompletion[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCertificate, setSelectedCertificate] = useState<any>(null);
  const [certificateTemplate, setCertificateTemplate] = useState<any>(null);
  const [showCertificate, setShowCertificate] = useState(false);

  useEffect(() => {
    fetchCompletions();
  }, []);

  const fetchCompletions = async () => {
    try {
      setLoading(true);
      
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) throw new Error('User not authenticated');

      const { data: completionsData, error: completionsError } = await supabase
        .from('training_completions')
        .select(`
          *,
          training_modules (
            title_en,
            title_el,
            description_en,
            description_el
          )
        `)
        .eq('user_id', user.id)
        .order('completed_at', { ascending: false });

      if (completionsError) throw completionsError;
      console.log('Fetched completions:', completionsData);
      setCompletions((completionsData || []) as TrainingCompletion[]);

    } catch (error) {
      console.error('Error fetching completions:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadCertificate = async (completion: TrainingCompletion) => {
    try {
      // Fetch certificate template
      const { data: template } = await supabase
        .from('certificate_templates')
        .select('*')
        .eq('module_id', completion.module_id)
        .maybeSingle();

      setCertificateTemplate(template);
      setSelectedCertificate(completion);
      setShowCertificate(true);
    } catch (error) {
      console.error('Error loading certificate:', error);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const getBadgeStats = () => {
    const stats = {
      gold: completions.filter(c => c.badge_level === 'gold').length,
      silver: completions.filter(c => c.badge_level === 'silver').length,
      bronze: completions.filter(c => c.badge_level === 'bronze').length,
      total: completions.filter(c => c.badge_level !== null).length
    };
    return stats;
  };

  const badgeStats = getBadgeStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading certificates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/training')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          {language === 'en' ? 'Back' : 'Πίσω'}
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            {language === 'en' ? '🏆 My Certificates' : '🏆 Τα Πιστοποιητικά μου'}
          </h1>
          <p className="text-muted-foreground mt-2">
            {language === 'en' 
              ? 'View and download your training certificates and badges'
              : 'Δείτε και κατεβάστε τα πιστοποιητικά και τα μετάλλια σας'
            }
          </p>
        </div>
      </div>

      {/* Badge Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="flex items-center p-6">
            <Trophy className="h-8 w-8 text-yellow-500 mr-4" />
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                {language === 'en' ? 'Gold Badges' : 'Χρυσά Μετάλλια'}
              </p>
              <p className="text-2xl font-bold">{badgeStats.gold}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="flex items-center p-6">
            <Award className="h-8 w-8 text-gray-400 mr-4" />
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                {language === 'en' ? 'Silver Badges' : 'Ασημένια Μετάλλια'}
              </p>
              <p className="text-2xl font-bold">{badgeStats.silver}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center p-6">
            <Award className="h-8 w-8 text-orange-600 mr-4" />
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                {language === 'en' ? 'Bronze Badges' : 'Χάλκινα Μετάλλια'}
              </p>
              <p className="text-2xl font-bold">{badgeStats.bronze}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center p-6">
            <Award className="h-8 w-8 text-blue-600 mr-4" />
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                {language === 'en' ? 'Total Badges' : 'Συνολικά Μετάλλια'}
              </p>
              <p className="text-2xl font-bold">{badgeStats.total}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Certificates List */}
      <div className="space-y-4">
        <h2 className="text-2xl font-semibold">
          {language === 'en' ? 'Training Completions' : 'Ολοκληρώσεις Εκπαίδευσης'}
        </h2>
        
        {completions.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center p-8">
              <Award className="h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold text-muted-foreground mb-2">
                {language === 'en' ? 'No certificates yet' : 'Δεν υπάρχουν πιστοποιητικά ακόμα'}
              </h3>
              <p className="text-muted-foreground text-center">
                {language === 'en' 
                  ? 'Complete training modules to earn certificates and badges'
                  : 'Ολοκληρώστε εκπαιδευτικά μοντέλα για να κερδίσετε πιστοποιητικά και μετάλλια'
                }
              </p>
              <Button 
                className="mt-4" 
                onClick={() => navigate('/training')}
              >
                {language === 'en' ? 'Start Training' : 'Έναρξη Εκπαίδευσης'}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {completions.map((completion) => (
              <Card key={completion.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">
                        {language === 'en' 
                          ? completion.training_modules.title_en 
                          : completion.training_modules.title_el
                        }
                      </CardTitle>
                      <CardDescription>
                        {language === 'en' 
                          ? completion.training_modules.description_en 
                          : completion.training_modules.description_el
                        }
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  {/* Badge Display */}
                  <BadgeDisplay 
                    badgeLevel={completion.badge_level}
                    score={completion.quiz_score}
                    certificateUrl={completion.certificate_url}
                  />
                  
                  {/* Completion Details */}
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      {new Date(completion.completed_at).toLocaleDateString()}
                    </div>
                    <div>
                      {completion.time_spent_minutes} {language === 'en' ? 'minutes' : 'λεπτά'}
                    </div>
                  </div>

                  {/* Certificate Download */}
                  {completion.certificate_url && (
                    <Button 
                      onClick={() => downloadCertificate(completion)}
                      variant="outline"
                      size="sm"
                      className="w-full"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      {language === 'en' ? 'View Certificate' : 'Προβολή Πιστοποιητικού'}
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Certificate Print Dialog */}
      <Dialog open={showCertificate} onOpenChange={setShowCertificate}>
        <DialogContent className="max-w-[900px] max-h-[90vh] overflow-y-auto print:max-w-full print:max-h-full">
          <div className="print:hidden flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold">{language === 'en' ? 'Certificate' : 'Πιστοποιητικό'}</h2>
            <div className="flex gap-2">
              <Button onClick={handlePrint} size="sm">
                <Printer className="h-4 w-4 mr-2" />
                {language === 'en' ? 'Print' : 'Εκτύπωση'}
              </Button>
              <Button onClick={() => setShowCertificate(false)} variant="ghost" size="sm">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {selectedCertificate && (
            <div 
              className="bg-white p-16 print:p-20"
              style={{
                backgroundImage: certificateTemplate?.canvas_data?.background ? `url(${certificateTemplate.canvas_data.background})` : undefined,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }}
            >
              <div className="text-center space-y-6">
                {/* Logo */}
                <div className="flex justify-center mb-8">
                  {certificateTemplate?.canvas_data?.elements?.logo?.url ? (
                    <img 
                      src={certificateTemplate.canvas_data.elements.logo.url} 
                      alt="Logo" 
                      className="h-16 w-auto object-contain" 
                    />
                  ) : (
                    <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center">
                      <svg width="32" height="32" viewBox="0 0 16 16" className="text-white">
                        <g stroke="currentColor" strokeWidth="1.5" fill="none">
                          <path d="M3 6 L5 8 L3 10" />
                          <path d="M13 6 L11 8 L13 10" />
                          <path d="M6 6 L10 10" />
                          <path d="M10 6 L6 10" />
                        </g>
                      </svg>
                    </div>
                  )}
                </div>

                {/* Title */}
                <div>
                  <h1 className="text-4xl font-bold text-gray-900 mb-2">
                    {certificateTemplate?.canvas_data?.elements?.title || 'CERTIFICATE'}
                  </h1>
                  <h2 className="text-xl font-semibold text-orange-500">
                    {certificateTemplate?.canvas_data?.elements?.subtitle || 'OF COMPLETION'}
                  </h2>
                </div>

                {/* Recipient */}
                <div className="my-8">
                  <p className="text-base text-gray-600 mb-3">
                    {language === 'en' ? 'This is to certify that' : 'Πιστοποιείται ότι'}
                  </p>
                  <h3 className="text-3xl font-bold text-gray-900 mb-3">
                    {selectedCertificate.user?.full_name || 'Employee Name'}
                  </h3>
                  <p className="text-base text-gray-600 mb-2">
                    {language === 'en' ? 'has successfully completed' : 'ολοκλήρωσε επιτυχώς'}
                  </p>
                  <h4 className="text-2xl font-semibold text-gray-800">
                    {language === 'en' 
                      ? selectedCertificate.training_modules?.title_en 
                      : selectedCertificate.training_modules?.title_el
                    }
                  </h4>
                </div>

                {/* Badge */}
                {selectedCertificate.badge_level && (
                  <div className="flex justify-center space-x-3 my-6">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      selectedCertificate.badge_level === 'gold' ? 'bg-yellow-500' :
                      selectedCertificate.badge_level === 'silver' ? 'bg-gray-400' :
                      'bg-amber-600'
                    }`}>
                      <span className="text-xl text-white font-bold">
                        {selectedCertificate.badge_level === 'gold' ? 'G' :
                         selectedCertificate.badge_level === 'silver' ? 'S' : 'B'}
                      </span>
                    </div>
                  </div>
                )}

                {/* Score */}
                <div className="my-6">
                  <p className="text-lg text-gray-700">
                    {language === 'en' ? 'Score:' : 'Βαθμολογία:'} <span className="font-bold">{selectedCertificate.quiz_score}%</span>
                  </p>
                  <p className="text-sm text-gray-500">
                    {language === 'en' ? 'Completed on' : 'Ολοκληρώθηκε στις'} {new Date(selectedCertificate.completed_at).toLocaleDateString()}
                  </p>
                </div>

                {/* Footer */}
                <div className="mt-12 pt-6 border-t border-gray-300">
                  <p className="text-gray-600 italic">
                    {certificateTemplate?.canvas_data?.elements?.footer || 'OVIT HR Department'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CertificatesPage;